import { getChainName } from '@ui/constants/chain';
import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useEntryPriceAfterSpread } from '@ui/features/Trade/hooks/useEntryPriceAfterSpread';
import { useInvalidatePositionQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getTradingPortalFacet } from '@ui/services/contract/contract-instances/getTradingPortalFacet';
import type { Address } from '@ui/types/alias';
import type {
  GenericToastOptions,
  TxnMutateCallBackParams,
} from '@ui/types/tx';
import { buildTradeKey } from '@ui/utils/buildObjectKey';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { increaseGasLimit } from '@ui/utils/increaseGastLimit';
import { parseCustomErrorReason } from '@ui/utils/parseCustomErrorReason';
import { isNil, omit } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { bytesToHex } from 'viem';
import { useAccount, usePublicClient } from 'wagmi';

import { TradeFlowCheckerStore } from '../../../stores/TradeFlowCheckerStore';
import type { OpenMarketTradeParams } from '../../../types';
import { useCollateralToken } from '../../TradeModule/store/useCollateralToken';
import { useOpenMarketTradeParams } from './useOpenMarketTradeParams';
import { useOpenMarketTradeToastOpts } from './useOpenMarketTradeToastOpts';

type ContractOpenData = Omit<
  OpenMarketTradeParams,
  'value' | 'pythUpdateData' | 'oracleUpdateData'
>;

export const useOpenMarketTrade = ({
  onSuccess,
  onError,
  onSettled,
  onReceiptSettled,
  onMutate,
}: TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const invalidatePositionQuery = useInvalidatePositionQuery();
  const openMarketTradeParams = useOpenMarketTradeParams();
  const collateralToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const isNativeCollateralToken = collateralToken?.isNative ?? false;

  const {
    onErrorToastOpts,
    onMutateToastOpts,
    onSuccessToastOpts,
    onMutateToastOptsOneClick,
    onSuccessToastOptsOneClick,
  } = useOpenMarketTradeToastOpts();
  const mutationToastInfo = useMutationToast();

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingOpen: isSubmittingOneClickOpenV2,
    sendOpenPosition: sendOneClickOpenPositionV2,
  } = useOneClickTradingV2();

  const [isPendingOneClickTradeSettled, setIsPendingOneClickTradeSettled] =
    useState(false);

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccessToastOpts,
    onMutateToastOpts,
    onErrorToastOpts,
    onMutate,
    onSuccess: (txnHash) => {
      invalidatePositionQuery();

      onSuccess?.(txnHash);
    },
    onError,
    onSettled,
    onReceiptSettled: () => {
      invalidatePositionQuery();

      const randomBytes = crypto.getRandomValues(new Uint8Array(32));
      const randomHexValue = bytesToHex(randomBytes);

      const tradeKey = buildTradeKey({
        pairBase: openMarketTradeParams?.pairBase,
        isLong: openMarketTradeParams?.isLong,
        stopLoss: openMarketTradeParams?.stopLoss,
        takeProfit: openMarketTradeParams?.takeProfit,
        tokenIn: openMarketTradeParams?.tokenIn,
        randomHexValue, // make sure unify
      });

      TradeFlowCheckerStore.getState().setValue(randomHexValue, {
        tradeKey,
        marketTradeReceiptSent: new Date().toISOString(), // Store receipt sent time
        chainId,
        chainName: getChainName(chainId),
      });

      onReceiptSettled?.();
    },
  });

  const client = usePublicClient({
    chainId,
  });
  const { address: account } = useAccount();
  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async () => {
    const leverupAddress = getContractAddress('LeverUp', chainId);
    if (isNil(openMarketTradeParams) || isNil(leverupAddress)) return;
    if (!client) return;

    const omitValue = omit(openMarketTradeParams, [
      'value',
      'pythUpdateData',
      'oracleUpdateData',
    ]) as ContractOpenData;

    // OneClick V2 path (priority: uses user wallet EIP-712 signing)
    if (canUseOneClickTradingV2 && !isNativeCollateralToken) {
      setIsPendingOneClickTradeSettled(true);
      mutationToastInfo.onMutate(onMutateToastOptsOneClick);
      onMutate?.();

      try {
        const intentHash = await sendOneClickOpenPositionV2(omitValue);

        const randomBytes = crypto.getRandomValues(new Uint8Array(32));
        const randomHexValue = bytesToHex(randomBytes);

        const tradeKey = buildTradeKey({
          pairBase: openMarketTradeParams.pairBase,
          isLong: openMarketTradeParams.isLong,
          stopLoss: openMarketTradeParams.stopLoss,
          takeProfit: openMarketTradeParams.takeProfit,
          tokenIn: openMarketTradeParams.tokenIn,
          randomHexValue,
        });

        TradeFlowCheckerStore.getState().setValue(randomHexValue, {
          tradeKey,
          marketTradeReceiptSent: new Date().toISOString(),
          chainId,
          chainName: getChainName(chainId),
        });

        onReceiptSettled?.();

        const tradingStatus = await pollV2IntentStatus(intentHash);
        if (!tradingStatus.success) {
          const parsedReason =
            parseCustomErrorReason(tradingStatus.reason) ??
            tradingStatus.reason ??
            'One-click trade execution failed';

          throw new Error(parsedReason);
        }

        invalidatePositionQuery();

        const txHash = tradingStatus.txnHash as Address;
        mutationToastInfo.onSuccess({
          ...onSuccessToastOptsOneClick,
          txHash,
        });

        onSuccess?.(txHash);

        return txHash;
      } catch (error) {
        const message =
          error instanceof Error
            ? extractErrorMessage(error.message) ?? error.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
        });
        onError?.(error);
        throw error;
      } finally {
        setIsPendingOneClickTradeSettled(false);
        mutationToastInfo.onSettled();
        onSettled?.();
      }
    }

    /* 
      On-chain path: when OneClick V2 is not available
    */

    const omitValueOnChain = omit(openMarketTradeParams, [
      'value',
      'pythUpdateData',
      'oracleUpdateData',
      'extraFee',
    ]);

    let estimateGasResult;
    const { isSuccess } = await simulate(async () => {
      const contract = getTradingPortalFacet({
        chainId,
        publicClient: client,
      });
      estimateGasResult = await contract.estimateGas.openMarketTradeV2(
        [
          omitValueOnChain,
          openMarketTradeParams.oracleUpdateData,
          openMarketTradeParams.extraFee,
        ],
        {
          account: account,
          value: openMarketTradeParams.value,
        },
      );
    });
    if (!isSuccess) return;

    // Calculate gas limit with 10% buffer
    const gasLimit = increaseGasLimit(estimateGasResult);

    return writeContract({
      abi: TradingPortalFacet,
      address: leverupAddress,
      functionName: 'openMarketTradeV2',
      args: [
        omitValueOnChain,
        openMarketTradeParams.oracleUpdateData,
        openMarketTradeParams.extraFee,
      ],
      value: openMarketTradeParams.value,
      gas: gasLimit,
    });
  }, [
    account,
    canUseOneClickTradingV2,
    isNativeCollateralToken,
    chainId,
    client,
    invalidatePositionQuery,
    mutationToastInfo,
    onError,
    onMutate,
    onMutateToastOptsOneClick,
    onReceiptSettled,
    onSettled,
    onSuccess,
    onSuccessToastOptsOneClick,
    openMarketTradeParams,
    sendOneClickOpenPositionV2,
    simulate,
    writeContract,
  ]);

  return useMemo(() => {
    return {
      data,
      write,
      isPending:
        isPending ||
        isSimulating ||
        isSubmittingOneClickOpenV2 ||
        isPendingOneClickTradeSettled,
    };
  }, [
    data,
    isPending,
    isSimulating,
    isSubmittingOneClickOpenV2,
    isPendingOneClickTradeSettled,
    write,
  ]);
};
